import React, { useState, ReactNode } from 'react'
import * as R from 'remeda'
import { SvgArrowTriangleDown } from '@chilipiper/icons/src/arrow/ArrowTriangleDown'
import { SvgArrowTriangleUp } from '@chilipiper/icons/src/arrow/ArrowTriangleUp'
import { useDebouncedCallback } from 'use-debounce'
import { CheckboxProps } from '../../checkbox'
import { ScreenReaderOnly, Text, Flex } from '../../../new/core-components'
import { Icon } from '../../../old/icon'
import { Option } from '../Option'
import { OptionProps } from '../types'

export interface OptionWithCategory extends Pick<OptionProps, 'renderAsSelected'> {
  category: string
  disabled?: boolean
  isSelected?: boolean
  label: string
  labelNode?: ReactNode
  value: string
}

type ExpandedCategoriesById = Record<string, boolean>

interface UseSelectWithCategoryControlParams {
  debounceDuration?: number
  initiallyExpandedCategories?: ExpandedCategoriesById
  isExpandableCategories?: boolean
  options: OptionWithCategory[]
  renderCategoryLabel: (category: string) => string
  sticky?: boolean
}

/** @deprecated - Use Select from `@chilipiper/design-system/src/new` instead */
export const useSelectWithCategoryControl = (params: UseSelectWithCategoryControlParams) => {
  const {
    options,
    renderCategoryLabel,
    isExpandableCategories,
    initiallyExpandedCategories = {},
    debounceDuration = 0,
  } = params
  const [filter, setFilterString] = useState('')

  const setFilter = useDebouncedCallback(setFilterString, debounceDuration)

  const [expandedCategories, setExpandedCategories] = useState<ExpandedCategoriesById>(
    initiallyExpandedCategories
  )

  const optionsByCategory = R.groupBy(options, o => o.category)

  const createOnCategoryClick = (category: string) => {
    setExpandedCategories(s => ({
      ...s,
      [category]: !s[category],
    }))
  }

  const renderedOptions = Object.keys(optionsByCategory).reduce((acc, category) => {
    const categoryOption = (
      <Option
        sticky={params.sticky}
        hidden={!!filter}
        value={category}
        key={category}
        isCategoryControl
        category={category}
        onClickCustom={isExpandableCategories ? () => createOnCategoryClick(category) : undefined}
      >
        <Flex w='100%'>
          <Text
            textStyle='list-group-heading'
            textOverflow='ellipsis'
            overflow='hidden'
            color='text/heading-moderate'
          >
            {renderCategoryLabel(category)}
          </Text>
          <ScreenReaderOnly>
            Press enter to {expandedCategories[category] ? 'collapse' : 'expand'} the category
          </ScreenReaderOnly>
          {isExpandableCategories && (
            <Flex ml='auto' alignItems='center' justifyContent='center'>
              <Icon
                w={4}
                h={4}
                pointer
                color='neutral-dark-32'
                icon={expandedCategories[category] ? SvgArrowTriangleUp : SvgArrowTriangleDown}
              />
            </Flex>
          )}
        </Flex>
      </Option>
    )

    const categoryOptions = optionsByCategory[category].map((option, idx) => {
      const hidden = filter
        ? !option.label.toLowerCase().includes(filter.toLowerCase())
        : isExpandableCategories && !expandedCategories[option.category]
      return (
        <Option
          hidden={hidden}
          key={`${category}_${option.value}_${idx}`}
          value={option.value}
          category={option.category}
          isWithBottomDivider
          disabled={option.disabled}
          renderAsSelected={option.renderAsSelected}
        >
          {option.labelNode ? (
            option.labelNode
          ) : (
            <Flex>
              <Text
                textOverflow='ellipsis'
                textStyle={option.isSelected ? 'item-selected' : 'item-unselected'}
                color='text/list-item'
                overflow='hidden'
              >
                {option.label}
              </Text>
            </Flex>
          )}
        </Option>
      )
    })

    return [...acc, categoryOption, ...categoryOptions]
  }, [] as React.ReactElement<any>[])

  return {
    renderedOptions,
    setFilter,
  }
}

interface Params {
  category?: string
  isCategoryControl?: boolean
  multiSelectValue: string[]
  optionsProps: OptionProps[]
}

export const getCategoryControlData = (params: Params) => {
  const { category, isCategoryControl, optionsProps, multiSelectValue } = params

  if (!isCategoryControl) {
    return {
      isCategoryControlChecked: false,
      categoryValues: [],
    }
  }

  const categoryOptions = optionsProps.filter(
    props => !props.isCategoryControl && props.category === category
  )

  const isSelected = (props: OptionProps) => multiSelectValue.includes(props.value)

  const isCategoryControlChecked: CheckboxProps['checked'] = isCategoryControl
    ? categoryOptions.every(isSelected)
      ? true
      : categoryOptions.some(isSelected)
        ? '-'
        : false
    : undefined

  const categoryValues = categoryOptions.filter(x => !x.disabled).map(x => x.value)

  return {
    isCategoryControlChecked,
    categoryValues,
  }
}
